import { OPEN_ACCOUNT_NAME_MAX_LENGTH } from '@/features/openTabs/constants'
import { logger } from '@/features/openTabs/logger'
import { OpenTabUpdateDocument } from '@/features/openTabs/mutations'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useMutation } from '@apollo/client'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface UseRenameOpenTabOptions {
    accountId?: string
    isOpen: boolean
    refreshAccount: () => Promise<void>
}

export const useRenameOpenTab = ({
    accountId,
    isOpen,
    refreshAccount,
}: UseRenameOpenTabOptions) => {
    const { t } = useTranslation('app')
    const handleError = useHandleErrorWithToast(logger)
    const [updateOpenTab] = useMutation(OpenTabUpdateDocument)
    const [renamingAccount, setRenamingAccount] = useState(false)
    const inFlightRef = useRef(false)
    const currentAccountRef = useRef({ accountId, isOpen })
    currentAccountRef.current = { accountId, isOpen }

    const renameAccount = useCallback(
        async (targetAccountId: string, name: string): Promise<boolean> => {
            const resolvedName = name.trim() || t('openTabs.createAccount.defaultName')
            if (
                currentAccountRef.current.accountId !== targetAccountId ||
                !currentAccountRef.current.isOpen ||
                resolvedName.length > OPEN_ACCOUNT_NAME_MAX_LENGTH ||
                inFlightRef.current
            ) {
                return false
            }

            inFlightRef.current = true
            setRenamingAccount(true)
            try {
                const result = await updateOpenTab({
                    variables: { openTabUuid: targetAccountId, input: { name: resolvedName } },
                })
                if (result.data?.openTabUpdate.uuid !== targetAccountId) {
                    throw new Error('Open tab rename returned no matching account.')
                }
                return true
            } catch (error) {
                void handleError(error)
                return false
            } finally {
                await refreshAccount().catch(() => undefined)
                inFlightRef.current = false
                setRenamingAccount(false)
            }
        },
        [handleError, refreshAccount, t, updateOpenTab]
    )

    return { renameAccount, renamingAccount }
}
